import { useState } from "react";
import {
  useCreateManualSocialOpportunityMutation,
  useCreateSocialPostMutation,
  useDismissSocialOpportunityMutation,
  useExportSocialPostMutation,
  useGenerateSocialPostMutation,
  useGetSocialOpportunitiesQuery,
  useGetSocialPostQuery,
  useGetSocialPostsQuery,
  useMarkSocialPostPostedMutation,
  usePatchSocialPostMutation,
  useRefreshSocialOpportunitiesMutation,
  useSaveSocialOpportunityMutation,
  useUseSocialOpportunityMutation,
} from "../store/api/mlbApi";

const MISSING_VECTOR_FRAGMENT = "No similarity vector exists";

const socialError = (error, fallback) =>
  error?.data?.error || error?.message || fallback;

const isAdminAuthError = (error) => {
  if (!error) return false;
  return (
    error?.data?.code === "ADMIN_REQUIRED" ||
    error?.status === 401 ||
    error?.status === 403
  );
};

const rejectedRefreshWarning = (rejectedCount) => {
  if (!rejectedCount) return null;
  return `${rejectedCount} seed${rejectedCount === 1 ? "" : "s"} skipped during refresh.`;
};

export const resolveAdminSocialAlerts = ({
  queryError,
  refreshError,
  refreshRejectedCount = 0,
  opportunities = [],
}) => {
  const queryMessage = socialError(queryError, null);
  const refreshMessage = socialError(refreshError, null);
  const rejectedWarning = rejectedRefreshWarning(refreshRejectedCount);

  if (isAdminAuthError(queryError) || isAdminAuthError(refreshError)) {
    return { error: queryMessage || refreshMessage, warning: null };
  }
  if (queryMessage) {
    return { error: queryMessage, warning: rejectedWarning };
  }
  if (
    refreshMessage &&
    String(refreshMessage).includes(MISSING_VECTOR_FRAGMENT)
  ) {
    return { error: null, warning: rejectedWarning || refreshMessage };
  }
  if (refreshMessage && opportunities.length > 0) {
    return { error: null, warning: refreshMessage };
  }
  if (refreshMessage) {
    return { error: refreshMessage, warning: rejectedWarning };
  }
  return { error: null, warning: rejectedWarning };
};

const useMappedMutation = (useMutation, actionName, fallback) => {
  const [mutate, state] = useMutation();

  const run = async (payload) => {
    try {
      const data = await mutate(payload).unwrap();
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: socialError(error, fallback),
        code: error?.data?.code,
      };
    }
  };

  return {
    [actionName]: run,
    isLoading: state.isLoading,
    error: socialError(state.error, null),
  };
};

export const useAdminSocial = (filters = {}) => {
  const opportunitiesQuery = useGetSocialOpportunitiesQuery(filters);
  const [refreshMutate, refreshState] = useRefreshSocialOpportunitiesMutation();
  const [refreshRejectedCount, setRefreshRejectedCount] = useState(0);
  const opportunities = opportunitiesQuery.data?.opportunities || [];

  const refreshOpportunities = async (payload) => {
    try {
      const data = await refreshMutate(payload).unwrap();
      setRefreshRejectedCount(data?.rejected?.length || 0);
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        error: socialError(error, "Failed to refresh social opportunities"),
        code: error?.data?.code,
      };
    }
  };

  const { error, warning } = resolveAdminSocialAlerts({
    queryError: opportunitiesQuery.error,
    refreshError: refreshState.error,
    refreshRejectedCount,
    opportunities,
  });

  return {
    opportunities,
    isLoading: opportunitiesQuery.isLoading,
    isRefreshing: refreshState.isLoading,
    error,
    warning,
    refetch: opportunitiesQuery.refetch,
    refreshOpportunities,
  };
};

export const useCreateManualSocialOpportunity = () =>
  useMappedMutation(
    useCreateManualSocialOpportunityMutation,
    "createManualOpportunity",
    "Failed to create social opportunity"
  );

export const useDismissSocialOpportunity = () =>
  useMappedMutation(
    useDismissSocialOpportunityMutation,
    "dismissOpportunity",
    "Failed to dismiss social opportunity"
  );

export const useSaveSocialOpportunity = () =>
  useMappedMutation(
    useSaveSocialOpportunityMutation,
    "saveOpportunity",
    "Failed to save social opportunity"
  );

export const useMarkSocialOpportunityUsed = () =>
  useMappedMutation(
    useUseSocialOpportunityMutation,
    "useOpportunity",
    "Failed to mark social opportunity used"
  );

export const useCreateSocialPost = () =>
  useMappedMutation(
    useCreateSocialPostMutation,
    "createPost",
    "Failed to create social post"
  );

export const useAdminSocialPost = (id, options) => {
  const { data, error, isLoading, refetch } = useGetSocialPostQuery(id, options);
  return {
    post: data?.post || null,
    snapshot: data?.snapshot || null,
    error: socialError(error, null),
    isLoading,
    refetch,
  };
};

export const useAdminSocialPosts = (filters = {}) => {
  const { data, error, isLoading, refetch } = useGetSocialPostsQuery(filters);
  return {
    posts: data?.posts || [],
    error: socialError(error, null),
    isLoading,
    refetch,
  };
};

export const useGenerateSocialPost = () =>
  useMappedMutation(
    useGenerateSocialPostMutation,
    "generatePost",
    "Failed to generate social post"
  );

export const useExportSocialPost = () =>
  useMappedMutation(
    useExportSocialPostMutation,
    "exportPost",
    "Failed to export social post"
  );

export const useMarkSocialPostPosted = () =>
  useMappedMutation(
    useMarkSocialPostPostedMutation,
    "markPostPosted",
    "Failed to mark social post posted"
  );

export const usePatchSocialPost = () =>
  useMappedMutation(
    usePatchSocialPostMutation,
    "patchPost",
    "Failed to update social post"
  );
